import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardMedia,
  CardActions,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  DragHandle as DragHandleIcon,
} from '@mui/icons-material';

interface MultiPhotoUploadProps {
  photos: string[];
  onPhotosChange: (photos: string[]) => void;
  onUpload: (file: File) => Promise<string>;
  maxPhotos?: number;
  disabled?: boolean;
}

export const MultiPhotoUpload: React.FC<MultiPhotoUploadProps> = ({
  photos,
  onPhotosChange,
  onUpload,
  maxPhotos = 10,
  disabled = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Check if adding these files would exceed the limit
    if (photos.length + files.length > maxPhotos) {
      setError(`You can only upload up to ${maxPhotos} photos`);
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file`);
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} is too large (max 5MB)`);
        }

        return await onUpload(file);
      });

      const newPhotoUrls = await Promise.all(uploadPromises);
      onPhotosChange([...photos, ...newPhotoUrls]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photos');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
    setError(null);
  };

  const handleMovePhoto = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= photos.length) return;
    
    const newPhotos = [...photos];
    const [movedPhoto] = newPhotos.splice(fromIndex, 1);
    newPhotos.splice(toIndex, 0, movedPhoto);
    onPhotosChange(newPhotos);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Property Photos
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Upload up to {maxPhotos} photos. The first photo will be used as the main image.
        </Typography>
      </Box>

      {photos.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {photos.map((photo, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={photo}
                  alt={`Property photo ${index + 1}`}
                  sx={{ objectFit: 'cover' }}
                />
                
                {index === 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                    }}
                  >
                    Main Photo
                  </Box>
                )}

                <CardActions sx={{ justifyContent: 'space-between', p: 1 }}>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleMovePhoto(index, index - 1)}
                      disabled={index === 0 || disabled}
                    >
                      ←
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleMovePhoto(index, index + 1)}
                      disabled={index === photos.length - 1 || disabled}
                    >
                      →
                    </IconButton>
                  </Box>
                  
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePhoto(index)}
                    disabled={disabled}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ textAlign: 'center' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={disabled || isUploading || photos.length >= maxPhotos}
        />

        <Button
          variant="outlined"
          startIcon={isUploading ? <CircularProgress size={20} /> : <PhotoCameraIcon />}
          onClick={handleButtonClick}
          disabled={disabled || isUploading || photos.length >= maxPhotos}
          sx={{ mb: 1 }}
        >
          {isUploading ? 'Uploading...' : photos.length === 0 ? 'Upload Photos' : 'Add More Photos'}
        </Button>

        <Typography variant="caption" display="block" color="text.secondary">
          JPG, PNG or GIF. Max size 5MB per photo. {photos.length}/{maxPhotos} photos uploaded.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default MultiPhotoUpload;